import { TextField } from '@mui/material';
import React, { useCallback, useState, useEffect } from 'react';
import { Search, Clear } from '@mui/icons-material';
import { InputAdornment, IconButton } from '@mui/material';

interface SearchInputProps {
    placeholder: string;
    defaultValue?: string;
    onChange: (value: string) => void;
    onClear: () => void;
    disabled?: boolean;
    minLength?: number;
    maxLength?: number;
    maxWidth?: string;
}

const SearchInput: React.FC<SearchInputProps> = ({
    defaultValue,
    placeholder,
    onChange,
    onClear,
    disabled,
    minLength = 3,
    maxLength,
    maxWidth = '400px',
}) => {
    const [searchInputValue, setSearchInputValue] = useState<string>(defaultValue || '');

    // Sync local state when defaultValue changes (e.g., from URL)
    useEffect(() => {
        setSearchInputValue(defaultValue || '');
    }, [defaultValue]);

    const handleSearchChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            setSearchInputValue(value);

            // Only clear search when input is empty
            if (value.length === 0) {
                onChange('');
            }
        },
        [onChange]
    );

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                const value = searchInputValue.trim();

                if (value.length >= minLength && (!maxLength || value.length <= maxLength)) {
                    onChange(value);
                } else if (value.length === 0) {
                    onChange('');
                }
            }
        },
        [onChange, searchInputValue, minLength, maxLength]
    );

    const handleClearSearch = useCallback(() => {
        setSearchInputValue('');
        onClear();
    }, [onClear]);

    return (
        <TextField
            size="small"
            data-testid="search-input"
            fullWidth
            placeholder={placeholder}
            value={searchInputValue}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            variant="outlined"
            sx={{ maxWidth }}
            disabled={disabled}
            slotProps={{
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <Search fontSize="small" sx={{ color: 'devblue.60' }} />
                        </InputAdornment>
                    ),
                    endAdornment: searchInputValue ? (
                        <InputAdornment position="end">
                            <IconButton
                                data-testid="clear-search-button"
                                size="small"
                                onClick={handleClearSearch}
                                edge="end"
                                aria-label="clear search"
                            >
                                <Clear fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    ) : null,
                },
                htmlInput: {
                    maxLength,
                },
            }}
        />
    );
};

export default SearchInput;
